import React, { useCallback, useEffect, useState } from 'react';
import { FiRefreshCw, FiAlertTriangle, FiPercent, FiX, FiPlus, FiDollarSign } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { financeAPI } from '../../services/api';
import * as fs from './financeStyles';

const emptyForm = () => ({ entity: '', fiscal_year: new Date().getFullYear(), estimated_annual_tax: '', currency: 'USD', notes: '' });

const ZimbabweTax = () => {
  const { isFinanceLeadership } = useAuth();
  const [entities, setEntities] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [entRes, curRes, estRes] = await Promise.all([
        financeAPI.entities.list(), financeAPI.currencies.list(), financeAPI.zimbabweTaxEstimates.list(),
      ]);
      setEntities(entRes.data.results || entRes.data || []);
      setCurrencies(curRes.data.results || curRes.data || []);
      setEstimates(estRes.data.results || estRes.data || []);
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.detail || 'Failed to load Zimbabwe tax estimates.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitForm = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await financeAPI.zimbabweTaxEstimates.create(form);
      setForm(emptyForm());
      setShowForm(false);
      load();
    } catch (e2) {
      setError(e2?.response?.data?.error || JSON.stringify(e2?.response?.data) || 'Failed to create tax estimate.');
    } finally {
      setSaving(false);
    }
  };

  const recordPayment = async (qpd) => {
    const amount = window.prompt(`Amount paid for QPD ${qpd.qpd_number} (due ${qpd.amount_due_this_qpd})?`);
    if (!amount) return;
    const paidDate = window.prompt('Paid date (YYYY-MM-DD)?', new Date().toISOString().slice(0, 10));
    if (!paidDate) return;
    setBusyId(qpd.id);
    setError(null);
    try {
      await financeAPI.zimbabweQpdPayments.recordPayment(qpd.id, { amount, paid_date: paidDate });
      load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to record payment.');
    } finally {
      setBusyId(null);
    }
  };

  const entityName = (id) => entities.find((e) => e.id === id)?.name || `#${id}`;

  return (
    <div style={fs.page}>
      <div style={fs.pageHeader}>
        <div>
          <h1 style={fs.pageTitle}><FiPercent size={22} />Zimbabwe Tax (QPD)</h1>
          <p style={fs.pageSubtitle}>Estimated annual tax liability tracked against ZIMRA's four Quarterly Payment Dates (10/25/30/35% cumulative). Confirm current-year rates with a local tax advisor.</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {isFinanceLeadership() && <button onClick={() => setShowForm((v) => !v)} style={fs.primaryButton}><FiPlus size={14} />New Estimate</button>}
          <button onClick={load} title="Refresh" style={fs.iconButton}><FiRefreshCw size={15} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} /></button>
        </div>
      </div>

      {error && (
        <div style={fs.errorBanner}>
          <FiAlertTriangle size={18} /><span>{error}</span>
          <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: fs.colors.red, cursor: 'pointer' }}><FiX size={16} /></button>
        </div>
      )}

      {showForm && (
        <form onSubmit={submitForm} style={{ ...fs.card, marginBottom: 22, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          <div><div style={fs.label}>Entity</div>
            <select required value={form.entity} onChange={(e) => setForm({ ...form, entity: e.target.value })} style={{ ...fs.select, width: '100%' }}>
              <option value="">Select…</option>{entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
          <div><div style={fs.label}>Fiscal Year</div><input required type="number" value={form.fiscal_year} onChange={(e) => setForm({ ...form, fiscal_year: e.target.value })} style={{ ...fs.input, width: '100%' }} /></div>
          <div><div style={fs.label}>Estimated Annual Tax</div><input required type="number" step="0.01" value={form.estimated_annual_tax} onChange={(e) => setForm({ ...form, estimated_annual_tax: e.target.value })} style={{ ...fs.input, width: '100%' }} /></div>
          <div><div style={fs.label}>Currency</div>
            <select required value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} style={{ ...fs.select, width: '100%' }}>
              <option value="">Select…</option>{currencies.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
            </select>
          </div>
          <div style={{ gridColumn: '1 / -1' }}><div style={fs.label}>Notes</div><textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} style={{ ...fs.input, width: '100%', resize: 'vertical' }} /></div>
          <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
            <button type="submit" disabled={saving} style={fs.primaryButton}>{saving ? 'Saving…' : 'Create Estimate'}</button>
          </div>
        </form>
      )}

      {loading ? (
        <div style={fs.loadingState}>Loading…</div>
      ) : estimates.length === 0 ? (
        <div style={fs.emptyState}><FiPercent size={44} style={{ marginBottom: 16 }} /><div style={{ fontSize: 15, color: fs.colors.textGhost }}>No tax estimates yet.</div></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {estimates.map((est) => {
            const expanded = expandedId === est.id;
            return (
              <div key={est.id} style={fs.card}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, cursor: 'pointer' }} onClick={() => setExpandedId(expanded ? null : est.id)}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <span style={{ color: fs.colors.textBright, fontWeight: 600, fontSize: 14 }}>{entityName(est.entity)} — FY{est.fiscal_year}</span>
                      <span style={{ color: fs.colors.textBright, fontWeight: 700 }}>{fs.money(est.estimated_annual_tax, est.currency)}</span>
                    </div>
                    {est.notes && <div style={{ color: fs.colors.textFaint, fontSize: 12, marginTop: 4 }}>{est.notes}</div>}
                  </div>
                </div>
                {expanded && (
                  <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${fs.colors.cardBorder}` }}>
                    <table style={{ width: '100%', fontSize: 12 }}>
                      <thead>
                        <tr>
                          {['QPD', 'Cumulative %', 'Due Date', 'Amount Due', 'Status', 'Paid', ''].map((h) => <th key={h} style={{ ...fs.th, padding: '4px 8px' }}>{h}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {(est.qpd_payments || []).map((q) => {
                          const busy = busyId === q.id;
                          return (
                            <tr key={q.id}>
                              <td style={{ padding: '4px 8px', color: fs.colors.text }}>QPD {q.qpd_number}</td>
                              <td style={{ padding: '4px 8px', color: fs.colors.text }}>{q.cumulative_percent_due}%</td>
                              <td style={{ padding: '4px 8px', color: fs.colors.text }}>{q.due_date}</td>
                              <td style={{ padding: '4px 8px', color: fs.colors.text }}>{fs.money(q.amount_due_this_qpd, est.currency)}</td>
                              <td style={{ padding: '4px 8px' }}><span style={fs.badge(fs.STATUS_COLORS[q.status] || '#888')}>{q.status}</span></td>
                              <td style={{ padding: '4px 8px', color: fs.colors.text }}>{q.status === 'PAID' ? `${fs.money(q.paid_amount, est.currency)} on ${q.paid_date}` : '—'}</td>
                              <td style={{ padding: '4px 8px' }} onClick={(e) => e.stopPropagation()}>
                                {q.status === 'PENDING' && isFinanceLeadership() && (
                                  <button onClick={() => recordPayment(q)} disabled={busy} style={fs.successButton}><FiDollarSign size={12} style={{ marginRight: 4, verticalAlign: -1 }} />Record Payment</button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ZimbabweTax;
